import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {AppContext} from '../../../App'
import AddAlertIcon from '../../../assets/icons/AddAlertIcon'
import {ListItemIcon, ListItemText} from '@mui/material'
import {addUserAlert} from '../../../reducer/alerts.reducer'
import {
    createUserAlertFromSnapshot,
    firstIndicatorOutputCodeForAlertUi,
    getCreateUserAlertRequestObject,
    getOperatorSource,
    getUserAlertMessageFromCondition
} from '../../../util/alerts'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import {useUser} from '../../../context/UserContext'
import {FeatureCategory, FeatureSubCategory} from '../../adminUserRestrictions/Restrictions.constants'
import {Group, setFeatureRestrictionDialog} from '../../../reducer/user.reducer'
import { FeatureCode } from '../../../util/RestrictionFeatureCode'
import { createAlert } from '../../../services/apis/alerts'
import commonActions from '../../../actions/common.actions'
import { selectIndicators } from '../../../selectors/chart'
import { SeriesGroup } from '../chart.typings'
import { SeriesGroupCategory } from '../../../pages/home'
import { getBaseSecurityKeyFromComparableToken, getSecurityAsync } from '../../../util/security'
import { convertMapToKeyValueArray } from '../../../util/common'
import { AlertExpression_Type, UserAlert } from '../../../proto/generated/nexus'

interface AlertMenuItemProps {
    viewKey: string
    disabled?: boolean
    customPriceValue?: string
    handleCloseMenu: Function
    symbolName: string | undefined
    seriesGroup?: SeriesGroup
}

function AlertContextMenuItem(props: AlertMenuItemProps) {
    const dispatch = useDispatch()
    const indicators = useSelector(selectIndicators)
    const {viewKey, customPriceValue, handleCloseMenu, symbolName, seriesGroup} = props
    const appContext = useContext(AppContext)
    const {user: {hasAccess}} = useUser()
    const chartSnapshot = useMemo(()=>{
        if(appContext?.getChartSnapshot?.[viewKey]) {
            return (appContext.getChartSnapshot[viewKey] as any)()
        }
        return undefined
    }, [appContext.getChartSnapshot, viewKey])
    const [price, setPrice] = useState(customPriceValue !== undefined ? Number(customPriceValue) : undefined)
    
    useEffect(()=>{
        if(chartSnapshot && !customPriceValue) {
            setPrice(chartSnapshot.data.close)
        }
    }, [chartSnapshot])

    const adaptAlertForPaneSeriesGroup = useCallback(async (baseAlert: UserAlert): Promise<UserAlert> => {
        if (!seriesGroup || seriesGroup.primary) return baseAlert
        const sac = baseAlert.condition?.securityAlertCondition
        if (!sac?.lhsExpression) return baseAlert

        if (seriesGroup.category === SeriesGroupCategory.SERIES_VOLUME) {
            const lhs = sac.lhsExpression
            const nextLhs = lhs.type === AlertExpression_Type.SPREAD_SECURITY
                ? {
                    ...lhs,
                    spreadSecurity: lhs.spreadSecurity ? { ...lhs.spreadSecurity, source: 'volume' } : lhs.spreadSecurity
                }
                : lhs.type === AlertExpression_Type.SIMPLE_SECURITY
                  ? {
                      ...lhs,
                      simpleSecurity: lhs.simpleSecurity ? { ...lhs.simpleSecurity, source: 'volume' } : lhs.simpleSecurity
                  }
                  : lhs
            return {
                ...baseAlert,
                condition: {
                    ...baseAlert.condition,
                    securityAlertCondition: { ...sac, lhsExpression: nextLhs }
                }
            }
        }

        if (seriesGroup.category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) {
            const cmpToken = seriesGroup.securityList?.[0]
            if (!cmpToken) return baseAlert
            const baseKey = getBaseSecurityKeyFromComparableToken(cmpToken) || cmpToken
            const sec = await getSecurityAsync(baseKey)
            if (!sec) return baseAlert
            const lhsExpression = {
                type: AlertExpression_Type.SIMPLE_SECURITY,
                simpleSecurity: {
                    security: { exchange: sec.exchange, code: sec.code },
                    source: getOperatorSource(sac.operator)
                },
                timeBucket: sac.lhsExpression.timeBucket,
                indicator: undefined,
                fixedValue: undefined,
                spreadSecurity: undefined
            }
            return {
                ...baseAlert,
                security: { exchange: sec.exchange, code: sec.code },
                condition: {
                    ...baseAlert.condition,
                    securityAlertCondition: { ...sac, lhsExpression }
                }
            }
        }

        if (seriesGroup.category === SeriesGroupCategory.SERIES_INDICATOR) {
            const indicatorDef = indicators.find((i) => i.code === seriesGroup.scriptCode)
            const output =
                firstIndicatorOutputCodeForAlertUi(indicatorDef?.outputs) ||
                Object.values(seriesGroup.outputValues || {})[0]?.code ||
                ''
            if (!output) return baseAlert
            const lhsExpression = {
                type: AlertExpression_Type.TECHNICAL_INDICATOR,
                indicator: {
                    indicatorCode: seriesGroup.scriptCode,
                    params: convertMapToKeyValueArray(seriesGroup.inputValues || {}),
                    output,
                    expression: sac.lhsExpression
                },
                timeBucket: undefined,
                simpleSecurity: undefined,
                spreadSecurity: undefined,
                fixedValue: undefined
            }
            return {
                ...baseAlert,
                condition: {
                    ...baseAlert.condition,
                    securityAlertCondition: { ...sac, lhsExpression }
                }
            }
        }

        return baseAlert
    }, [seriesGroup, indicators])

    const handleButtonClick = useCallback(async () => {
        if(!hasAccess(FeatureCategory.CHART, FeatureSubCategory.ALERTS)) {
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Alerts",
                group: Group.CHART,
                featureRestrictionCode:FeatureCode.CHART_ALERTS
            }))
            handleCloseMenu()
            return
        }
        
        AnalyticsManager.logEvent('Add Alert Clicked', {source: 'chart-grid'})
        const chartSnapshotMethod = appContext.getChartSnapshot[viewKey]
        const snapshot = chartSnapshotMethod ? chartSnapshotMethod() : null
        if (!snapshot) return
        if(price !== undefined) snapshot.data.close = Number(price)
        try {
            const baseAlert = await adaptAlertForPaneSeriesGroup(createUserAlertFromSnapshot(snapshot))
            const primary = snapshot.securityList?.[0]
            const security = primary
                ? { exchange: primary.exchange, code: primary.code }
                : baseAlert.security
            const generatedMessage = await getUserAlertMessageFromCondition(
                baseAlert.condition,
                symbolName || primary?.code || '',
                indicators,
                {
                    chartSnapshot: snapshot,
                    chartTimeBucket: snapshot.timeBucket ?? baseAlert.timeBucket,
                }
            )
            const alert = {
                ...baseAlert,
                security,
                message: generatedMessage || baseAlert.message
            }
            const chart = appContext.getChart(viewKey)
            const created = await createAlert(
                getCreateUserAlertRequestObject(alert, {
                    seriesGroups: chart?.chartConfig.seriesGroups,
                    indicators,
                })
            )
            dispatch(addUserAlert(created))
        } catch (e: any) {
            commonActions.setApiError(dispatch, {
                error: true,
                message: e?.message || 'Failed to create alert',
                errorType: 'error'
            })
        }
        handleCloseMenu()
    }, [appContext.getChartSnapshot, viewKey, price, dispatch, handleCloseMenu, hasAccess, symbolName, indicators, adaptAlertForPaneSeriesGroup])

    return (
        <>
            <ListItemIcon onClick={handleButtonClick}>
                <AddAlertIcon sx={{fontSize: '1.5rem !important'}}/>
            </ListItemIcon>
            <ListItemText onClick={handleButtonClick}>
                Add Alert on {symbolName ?? ''} at {customPriceValue??price}
            </ListItemText>
        </>
    )
}

export default React.memo(AlertContextMenuItem)